import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/services/api";
import type {
  EmailLogItem,
  EmailTimelineResponse,
  Page,
  ParticipantCohortItem,
  ParticipantTopicItem,
  ScheduledEmailItem,
  SuppressionItem,
} from "@/types";

/**
 * React Query hooks for the email sequence admin surfaces — the schedule (plan),
 * the log (audit), and the suppression list, plus the manual operations
 * (send-now / cancel / resend / ad-hoc / unsuppress). Wraps the API client and
 * owns query keys + invalidation-on-success. Follows the Support slice template.
 */

export interface ScheduleParams {
  status?: string;
  template_id?: string;
  user_id?: string;
  programme_id?: string;
  q?: string;
  page?: number;
  page_size?: number;
}

export const emailSeqKeys = {
  all: ["admin", "email-sequence"] as const,
  schedule: (p: ScheduleParams) => [...emailSeqKeys.all, "schedule", p] as const,
  log: (p: ScheduleParams) => [...emailSeqKeys.all, "log", p] as const,
  suppressions: () => [...emailSeqKeys.all, "suppressions"] as const,
};

export function useEmailSchedule(params: ScheduleParams = {}) {
  return useQuery<Page<ScheduledEmailItem>>({
    queryKey: emailSeqKeys.schedule(params),
    queryFn: () => api.getEmailSchedule({ page_size: 100, ...params }),
  });
}

export function useEmailLog(params: ScheduleParams = {}) {
  return useQuery<Page<EmailLogItem>>({
    queryKey: emailSeqKeys.log(params),
    queryFn: () => api.getEmailLog({ page_size: 100, ...params }),
  });
}

export function useSuppressions() {
  return useQuery<Page<SuppressionItem>>({
    queryKey: emailSeqKeys.suppressions(),
    queryFn: () => api.getEmailSuppressions({ page_size: 100 }),
  });
}

export function useParticipantEmailTimeline(userId: string | undefined) {
  return useQuery<EmailTimelineResponse>({
    queryKey: [...emailSeqKeys.all, "timeline", userId ?? ""],
    queryFn: () => api.getParticipantEmailTimeline(userId as string),
    enabled: Boolean(userId),
  });
}

export function useParticipantCohorts(userId: string | undefined) {
  return useQuery<ParticipantCohortItem[]>({
    queryKey: [...emailSeqKeys.all, "cohorts", userId ?? ""],
    queryFn: () => api.getParticipantCohorts(userId as string),
    enabled: Boolean(userId),
  });
}

export function useParticipantTopics(userId: string | undefined) {
  return useQuery<ParticipantTopicItem[]>({
    queryKey: [...emailSeqKeys.all, "topics", userId ?? ""],
    queryFn: () => api.getParticipantTopics(userId as string),
    enabled: Boolean(userId),
  });
}

function useInvalidateEmailSeq() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: emailSeqKeys.all });
}

export function useSendNow() {
  const invalidate = useInvalidateEmailSeq();
  return useMutation({
    mutationFn: (id: string) => api.sendScheduledEmailNow(id),
    onSuccess: invalidate,
  });
}

export function useCancelScheduled() {
  const invalidate = useInvalidateEmailSeq();
  return useMutation({
    mutationFn: (id: string) => api.cancelScheduledEmail(id),
    onSuccess: invalidate,
  });
}

export function useResendEmail() {
  const invalidate = useInvalidateEmailSeq();
  return useMutation({ mutationFn: (id: string) => api.resendEmailLog(id), onSuccess: invalidate });
}

export function useUnsuppressEmail() {
  const invalidate = useInvalidateEmailSeq();
  return useMutation({
    mutationFn: (email: string) => api.unsuppressEmail(email),
    onSuccess: invalidate,
  });
}

export function useAdHocSend() {
  const invalidate = useInvalidateEmailSeq();
  return useMutation({
    mutationFn: (payload: {
      template_id: string;
      context?: Record<string, unknown>;
      user_id?: string;
      programme_id?: string;
    }) => api.adHocSendEmail(payload),
    onSuccess: invalidate,
  });
}
